import { useMemo, useState } from "react";
import { IdentificationCard, SpinnerGap, ArrowRight } from "@phosphor-icons/react";
import { PURPOSES, GUEST_PAY_METHODS, PAY_STATUS, HEARD } from "@/lib/data";

const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
const today = () => iso(new Date());
const plus = (d, n) => {
  const x = new Date(`${d}T12:00:00`);
  x.setDate(x.getDate() + n);
  return iso(x);
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium" style={{ color: "var(--t-text)" }}>{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs" style={{ color: "var(--t-muted)" }}>{hint}</span>}
  </label>
);

const inputCls = "w-full h-12 rounded-xl border px-3.5 text-base outline-none bg-[var(--t-bg)] border-[var(--t-border)] text-[var(--t-text)] focus:ring-2 focus:ring-[var(--t-primary)]";

const Sel = ({ value, onChange, options, testId, placeholder = "Choose…" }) => (
  <select data-testid={testId} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export function CheckinForm({ form: settings, onSubmit, onReturning, disabled }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", check_in: today(), check_out: plus(today(), 2), guests: 1, purpose: "", payment_method: "", payment_status: "", heard: "", offers: true, notes: "", agree: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showReturn, setShowReturn] = useState(false);
  const [last4, setLast4] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v }));

  const nights = useMemo(() => Math.round((new Date(f.check_out) - new Date(f.check_in)) / 86400000), [f.check_in, f.check_out]);
  const needId = settings.id_nights > 0 && nights >= settings.id_nights;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.name.trim().length < 2) return setErr("Please enter your full name.");
    if (f.phone.replace(/\D/g, "").length < 6) return setErr("Please enter a phone or WhatsApp number.");
    if (!(nights >= 1)) return setErr("Check-out must be after check-in.");
    if (needId && !file) return setErr(`Please add an ID or passport photo (needed for stays of ${settings.id_nights}+ nights).`);
    if (!f.agree) return setErr("Please agree to the house rules.");
    setBusy(true);
    try {
      await onSubmit({ ...f, guests: Number(f.guests) }, needId ? file : null);
    } catch (e2) {
      setErr(e2.message || "Could not send the form.");
    } finally {
      setBusy(false);
    }
  };

  const doReturn = async () => {
    setErr("");
    if (!/^\d{4}$/.test(last4)) return setErr("Enter the last 4 digits of your phone.");
    setBusy(true);
    try {
      await onReturning(last4);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl border p-5 sm:p-6" style={{ background: "var(--t-card)", borderColor: "var(--t-border)" }} data-testid="checkin-form-card">
      <p className="label-xs" style={{ color: "var(--t-primary)" }}>Check in</p>
      <h3 className="mt-1 text-2xl font-medium" style={{ color: "var(--t-text)" }}>Tell us who's staying</h3>
      {settings.welcome && <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--t-muted)" }}>{settings.welcome}</p>}

      <button type="button" data-testid="returning-guest-toggle" onClick={() => setShowReturn((s) => !s)} className="mt-4 w-full rounded-2xl px-4 py-3 text-left text-sm font-medium transition-colors duration-200" style={{ background: "var(--t-soft)", color: "var(--t-text)" }}>
        Stayed with us before? <span style={{ color: "var(--t-primary)" }}>Skip the form →</span>
      </button>
      {showReturn && (
        <div className="mt-3 flex gap-2" data-testid="returning-guest-panel">
          <input data-testid="returning-last4-input" inputMode="numeric" maxLength={4} placeholder="Last 4 digits of phone" className={inputCls} value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, ""))} />
          <button type="button" disabled={busy || disabled} onClick={doReturn} data-testid="returning-submit-button" className="shrink-0 rounded-full px-5 font-semibold disabled:opacity-60" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>Go</button>
        </div>
      )}

      <form onSubmit={submit} className="mt-5 space-y-4" data-testid="checkin-form">
        <Field label="Full name"><input data-testid="checkin-name-input" autoComplete="name" className={inputCls} value={f.name} onChange={set("name")} /></Field>
        <Field label="Phone / WhatsApp"><input data-testid="checkin-phone-input" type="tel" autoComplete="tel" placeholder="+256 7…" className={inputCls} value={f.phone} onChange={set("phone")} /></Field>
        <Field label="Email (optional)"><input data-testid="checkin-email-input" type="email" autoComplete="email" className={inputCls} value={f.email} onChange={set("email")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Check-in"><input data-testid="checkin-date-in" type="date" className={inputCls} value={f.check_in} onChange={set("check_in")} /></Field>
          <Field label="Check-out"><input data-testid="checkin-date-out" type="date" className={inputCls} value={f.check_out} onChange={set("check_out")} /></Field>
        </div>
        <p data-testid="checkin-nights" className="text-sm font-semibold" style={{ color: "var(--t-primary)" }}>{nights >= 1 ? `${nights} night${nights > 1 ? "s" : ""}` : "Pick your dates"}</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Guests"><input data-testid="checkin-guests-input" type="number" min={1} max={50} className={inputCls} value={f.guests} onChange={set("guests")} /></Field>
          <Field label="Purpose"><Sel testId="checkin-purpose-select" value={f.purpose} onChange={set("purpose")} options={PURPOSES} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Paid with"><Sel testId="checkin-paymethod-select" value={f.payment_method} onChange={set("payment_method")} options={GUEST_PAY_METHODS} /></Field>
          <Field label="Payment"><Sel testId="checkin-paystatus-select" value={f.payment_status} onChange={set("payment_status")} options={PAY_STATUS} /></Field>
        </div>
        <Field label="How did you hear about us?"><Sel testId="checkin-heard-select" value={f.heard} onChange={set("heard")} options={HEARD} /></Field>
        {needId && (
          <Field label="ID or passport photo" hint={`Needed for stays of ${settings.id_nights}+ nights. Only your host can see it.`}>
            <label data-testid="checkin-id-upload" className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed px-4 py-4" style={{ borderColor: "var(--t-primary)", color: "var(--t-text)" }}>
              <IdentificationCard size={26} style={{ color: "var(--t-primary)" }} />
              <span className="text-sm">{file ? file.name : "Tap to take or choose a photo"}</span>
              <input data-testid="checkin-id-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </Field>
        )}
        <Field label="Anything we should know? (optional)"><textarea data-testid="checkin-notes-input" rows={2} className={`${inputCls} h-auto py-3`} value={f.notes} onChange={set("notes")} /></Field>
        <label className="flex items-start gap-3 text-sm" style={{ color: "var(--t-text)" }}>
          <input data-testid="checkin-offers-checkbox" type="checkbox" className="mt-1 h-5 w-5 accent-[var(--t-primary)]" checked={f.offers} onChange={set("offers")} />
          Send me offers for my next stay
        </label>
        <label className="flex items-start gap-3 text-sm" style={{ color: "var(--t-text)" }}>
          <input data-testid="checkin-agree-checkbox" type="checkbox" className="mt-1 h-5 w-5 accent-[var(--t-primary)]" checked={f.agree} onChange={set("agree")} />
          I agree to the house rules
        </label>
        {err && <p data-testid="checkin-error" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <button type="submit" disabled={busy || disabled} data-testid="checkin-submit-button" className="flex h-14 w-full items-center justify-center gap-2 rounded-full text-base font-semibold transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>
          {busy ? <SpinnerGap className="animate-spin" size={20} /> : <>Send & unlock house info <ArrowRight size={18} weight="bold" /></>}
        </button>
        <p className="text-xs leading-relaxed" style={{ color: "var(--t-muted)" }}>
          Your details go only to {settings.business || "your host"} for guest registration and safety. They are not shared with anyone else.
        </p>
      </form>
    </div>
  );
}
